/**
 * EventRouteLine — the course as a line: start, every pit stop in order, finish.
 *
 *   A ──────── B ──────── C ──────── D
 *
 * A list of stops says what exists; a line says what order you meet them in,
 * which is the question a participant is actually asking. The start and the
 * finish are part of it, because a stop means nothing except as a point between
 * those two.
 *
 * Each node names its place as well as itself. A stop's row stores only a name,
 * a description and a point, so the place is looked up from the coordinate;
 * start and finish already carry addresses and use those, trimmed to their
 * first two parts — a full Nominatim address is six clauses long and would
 * bury the label it is meant to support.
 *
 * The line has two shapes. Across the card while the nodes are few, and down
 * the page once there are too many of them to give each a readable column —
 * see MAX_INLINE_NODES. The phone has always used the second shape at any
 * count; a crowded course now gets it on a desktop too.
 */

import React, { useLayoutEffect, useRef, useState } from 'react';
import useReversePlace, { shortPlace } from './useReversePlace';

/**
 * How many nodes the line will carry across the card before it turns down the
 * page instead.
 *
 * The card gives every node an equal share of its ~780px and no minimum, so
 * the count alone decides how wide a column is. Measured on the real strip:
 * seven nodes leaves 87px and place names like "Karahalli Cross,
 * Chikkaballapur" start painting over the stop beside them; five and six clear
 * that bar but read a word to a line. Four keeps every column near 196px —
 * wide enough for a name and its place to sit on one or two lines, which is
 * the point of stating them side by side at all.
 *
 * Past it the line takes the shape the phone layout has always used. Desktop
 * has the vertical room, and a stop nobody can read is worth less than a
 * taller card.
 */
export const MAX_INLINE_NODES = 4;

/**
 * How much of a stop is shown before its Show more. A stop's name and note are
 * both free text an organiser typed, and an unbounded one turns a single node
 * into a wall that pushes the rest of the course off the screen.
 */
export const STOP_NAME_LINES = 2;
export const STOP_NOTE_LINES = 3;

function StopNode({ name, description, point }) {
  const place = useReversePlace(point);
  const nameRef = useRef(null);
  const noteRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  // One toggle opens the name and the note together — they describe one stop,
  // and two buttons on a node this small would crowd it. Measured only while
  // collapsed, for the same reason as ProseParagraph: expanded, nothing
  // overflows and the toggle would vanish with the stop still open.
  useLayoutEffect(() => {
    if (expanded) return undefined;
    const els = [nameRef.current, noteRef.current].filter(Boolean);
    if (els.length === 0) return undefined;
    const measure = () => setOverflowing(els.some(el => el.scrollHeight > el.clientHeight + 2));
    measure();
    if (typeof ResizeObserver !== 'function') return undefined;
    const ro = new ResizeObserver(measure);
    els.forEach(el => ro.observe(el));
    return () => ro.disconnect();
  }, [name, description, expanded]);

  const clamp = lines => (expanded ? {} : { className: ' evb-clamp', style: { '--evb-lines': lines } });
  const nameClamp = clamp(STOP_NAME_LINES);
  const noteClamp = clamp(STOP_NOTE_LINES);

  return (
    <li className="evb-route__node">
      {/* A ring with its own centre — a station on a line. Position is what
          identifies a node here, and the line already states it, so a letter
          was labelling something that was never ambiguous. */}
      <span className="evb-route__dot" aria-hidden="true" />
      <b ref={nameRef} className={`evb-route__name${nameClamp.className || ''}`} style={nameClamp.style}>
        {name}
      </b>
      {place && <span className="evb-route__place">{place}</span>}
      {description && (
        <span ref={noteRef} className={`evb-route__note${noteClamp.className || ''}`} style={noteClamp.style}>
          {description}
        </span>
      )}
      {overflowing && (
        <button
          type="button"
          className="evb-more evb-route__more"
          aria-expanded={expanded}
          onClick={() => setExpanded(open => !open)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </li>
  );
}

function EndNode({ label, address }) {
  return (
    <li className="evb-route__node">
      <span className="evb-route__dot evb-route__dot--end" aria-hidden="true" />
      <b className="evb-route__name">{label}</b>
      {address && <span className="evb-route__place">{shortPlace(address)}</span>}
    </li>
  );
}

export default function EventRouteLine({ stops, startAddress, endAddress }) {
  const items = Array.isArray(stops) ? stops.filter(Boolean) : [];
  if (items.length === 0) return null;

  // Order is the whole point, and `order_index` is what the organiser set by
  // dragging the stops around. Falling back to the array's own order keeps a
  // source that does not send the column working rather than empty.
  const ordered = [...items].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));

  const hasStart = Boolean(startAddress);
  const hasFinish = Boolean(endAddress);
  let index = 0;

  // The start and the finish take a column each, so they count toward how
  // crowded the line is exactly as a stop does.
  const nodeCount = ordered.length + (hasStart ? 1 : 0) + (hasFinish ? 1 : 0);
  const stacked = nodeCount > MAX_INLINE_NODES;

  return (
    <div className={stacked ? 'evb-route evb-route--stack' : 'evb-route'}>
      <span className="evb-route__k">Course</span>
      <ol className="evb-route__line">
        {hasStart && <EndNode key="start" label="Start" address={startAddress} />}

        {ordered.map(stop => (
          <StopNode
            key={stop.id ?? `${stop.name}-${index++}`}
            name={stop.name}
            description={stop.description}
            point={stop.point}
          />
        ))}

        {hasFinish && <EndNode key="finish" label="Finish" address={endAddress} />}
      </ol>
    </div>
  );
}
